import { useEffect, useRef, useState, type CSSProperties } from 'react';
import HolidayCalendar from '../../charts/HolidayCalendar/HolidayCalendar';
import ProjectScreen from '../../ProjectScreen/ProjectScreen';
import PhotoCredit from '../../ui/PhotoCredit/PhotoCredit';
import type { Project } from '../../../data/projects';
import styles from './ProjectVisual.module.css';

type VisualFormat = 'card' | 'wide' | 'hero' | 'lead';

const TALL_QUERY = '(max-width: 47.9375rem)';
const INITIAL_SETS_PER_LOOP = 1;

interface ProjectVisualProps {
    project: Project;
    format?: VisualFormat;
    screenAlt: string;
}

function ScreenOverlay({ project, alt }: { project: Project; alt: string }) {
    return (
        <div className={styles.screen}>
            <ProjectScreen project={project} alt={alt} />
        </div>
    );
}

function Filmstrip({ images }: { images: string[] }) {
    const stripRef = useRef<HTMLDivElement>(null);
    const [setsPerLoop, setSetsPerLoop] = useState(INITIAL_SETS_PER_LOOP);
    const rowImages = Array.from({ length: setsPerLoop * 2 }, () => images).flat();

    useEffect(() => {
        const strip = stripRef.current;
        const row = strip?.firstElementChild;
        if (!strip || !(row instanceof HTMLElement)) return;

        const coverStrip = () =>
            setSetsPerLoop((currentSets) => {
                const setWidth = row.offsetWidth / (currentSets * 2);
                return Math.ceil(strip.offsetWidth / setWidth) + 1;
            });

        const observer = new ResizeObserver(coverStrip);
        observer.observe(strip);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={stripRef} className={styles.filmstrip} style={{ '--sets-per-loop': setsPerLoop } as CSSProperties} aria-hidden="true">
            {[0, 1].map((row) => (
                <div key={row} className={styles.filmRow} data-row={row}>
                    {rowImages.map((image, index) => (
                        <img key={`${image}-${index}`} src={image} alt="" className={styles.filmFrame} loading="lazy" decoding="async" />
                    ))}
                </div>
            ))}
        </div>
    );
}

function Frames({ images, alt }: { images: string[]; alt: string }) {
    return (
        <div className={styles.frames}>
            {images.map((image, index) => (
                <img
                    key={image}
                    src={image}
                    alt={index === 0 ? alt : ''}
                    className={styles.frame}
                    style={{ '--index': index } as CSSProperties}
                    loading="lazy"
                    decoding="async"
                />
            ))}
        </div>
    );
}

export default function ProjectVisual({ project, format = 'card', screenAlt }: ProjectVisualProps) {
    const { visual } = project;

    return (
        <div className={styles.visual} data-kind={visual.kind} data-format={format}>
            {visual.kind === 'photo' && (
                <>
                    <img src={visual.image} alt="" className={styles.backdrop} loading={format === 'hero' ? 'eager' : 'lazy'} decoding="async" />
                    <ScreenOverlay project={project} alt={screenAlt} />
                    {visual.credit && <PhotoCredit source={visual.credit} />}
                </>
            )}
            {visual.kind === 'cover' && (
                <picture>
                    {visual.tallImage && <source media={TALL_QUERY} srcSet={visual.tallImage} />}
                    <img src={visual.image} alt={screenAlt} className={styles.backdrop} loading={format === 'hero' ? 'eager' : 'lazy'} decoding="async" />
                </picture>
            )}
            {visual.kind === 'filmstrip' && (
                <>
                    <Filmstrip images={visual.images} />
                    <ScreenOverlay project={project} alt={screenAlt} />
                </>
            )}
            {visual.kind === 'frames' && <Frames images={visual.images} alt={screenAlt} />}
            {visual.kind === 'calendar' && (
                <div className={styles.calendarPanel} data-theme="light">
                    <HolidayCalendar />
                </div>
            )}
        </div>
    );
}
